/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IStoreSelectorStateManager } from '@msdyn365-commerce-modules/bopis-utilities';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
// VSI Customization - end
import {
    HtmlHeadInclude,
    IImageData,
    ILinkData,
    RichTextComponent
} from '@msdyn365-commerce/core';
import { ICategoryUrl } from '@msdyn365-commerce/core-internal/dist/types/interfaces/ICategoryPathInterfaces';
// VSI Customization - starts
import { AsyncResult, AttributeValue, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { Dayjs } from 'dayjs';
import * as React from 'react';
import getHtmlParserValue from '../../Utilities/get-html-parser-value';
import { IBuyboxGetDimensionVariantAvailabilityActionData } from '../../data-actions/buybox-get-dimension-variants-availability-action';
import { IStoreSelectorState } from '../../data-actions/store-selector-state';
import _getDeliveryByMessage from '../../Utilities/get-delivery-by-message';

/** VSI Customization - Start */
import _getInventoryQuantity from '../../Utilities/get-inventory-quantity';
import { BuyboxProductConfigureMfrm } from './components/buybox-product-configure-mfrm';
/** VSI Customization - End */
import { IBuyboxViewPropsExtended } from './types';
import { IMfrmBuyboxData } from './mfrm-buybox.data';
import { IBuyboxGetDimensionPricingActionData } from '../../data-actions/buybox-get-dimension-pricing-action';

import CustomSkeleton from '../../themes/mattressfirm/custom-components/CustomSkeleton';

import { BuyboxCallChat } from './components/buybox-call-chat';
import { BuyboxAddToCart } from './components/buybox-add-to-cart';
import { BuyboxViewCartLink } from './components/buybox-view-cart-link';
import { BuyboxSocialShare } from './components/buybox-social-share';
import { BuyboxFindInStore } from './components/buybox-find-in-store';
import { BuyboxSleepExpertSummary } from './components/buybox-sleep-expert-summary';
import { BuyboxProgressiveLeasingEstimator } from './components/buybox-progressive-leasing-estimator';
import { BuyboxFinancingPerMonth } from './components/buybox-financing-per-month';
import { BuyboxDeliveryByMessage } from './components/buybox-devliery-by-message';
import { _smallParcelProductATPCallForInventoryStatus } from '../../Utilities/mfrm-buybox-utils';
import { ErrorBoundary } from '../../common/components/error-boundary';
import useVwoClientInstance from '../../common/hooks/useVwoClientInstance';

// TODO: remove this interface and all references to it
export interface IMfrmBuyBoxData extends IMfrmBuyboxData {
    productSpecificationData: AsyncResult<AttributeValue[]>;
    dimensionPrices: AsyncResult<IBuyboxGetDimensionPricingActionData[]>;
    allVariants: AsyncResult<IBuyboxGetDimensionVariantAvailabilityActionData[]>;
    storeSelectorStateManager: AsyncResult<IStoreSelectorStateManager>;
    storeSelectorState: AsyncResult<IStoreSelectorState>;
    categoryPaths: AsyncResult<ICategoryUrl[]>;
    allProductVariants: AsyncResult<SimpleProduct[]>;
}

export interface IBrandLinkData {
    linkText?: string;
    linkUrl: ILinkData;
    ariaLabel?: string;
    openInNewTab?: boolean;
}

export interface IVariantStockInfo {
    VariantRecID: number;
    OutOfStock: boolean;
}

export interface IBrandListData {
    brandName: string;
    brandLogo: IImageData;
    logoHeight?: number;
    brandLink?: IBrandLinkData;
    brandLeadtime?: string;
}

export interface IMfrmAtpMessage {
    Message?: string;
}

export interface IBadgeData {
    src: string;
    alt: string;
    start: Dayjs;
    end: Dayjs;
}

const setActiveSwatchSelected = (
    variantID: number,
    outOfStock: boolean,
    setOutOfStock: (outOfStock: boolean) => void,
    avList: IBuyboxGetDimensionVariantAvailabilityActionData[]
): void => {
    if (variantID < 0) {
        return;
    }
    if (avList?.length) {
        for (let variantsIDIter = 0; variantsIDIter < avList.length; variantsIDIter++) {
            if (avList[variantsIDIter].variantID === variantID) {
                if (outOfStock !== !avList[variantsIDIter].isAvailable) {
                    setOutOfStock(!avList[variantsIDIter].isAvailable);
                }
                return;
            }
        }
    }
};

// tslint:disable-next-line
const BuyboxView: React.FC<IBuyboxViewPropsExtended> = props => {
        const {
            ModuleProps,
            MediaGalleryContainerProps,
            ProductInfoContainerProps,
            addToCart,
            /*addToWishlist,*/
            findInStore,
            title,
            inventoryLabel,
            shopSimilarLook,
            keyInPrice,
            /*addToOrderTemplate*/
            productBadges,
            ratingComponent,
            tryInStoreComponent,
            promotionalMessage,
            customerSatisfactionScore,
            adjustableItem,
            pillow,
            protector,
            config: {
                progressiveLeasingEstimator,
                renderOOBMediaGallery
            },
            slots: { mfrmStoreSelector, mediaGallery, mfrmMediaGallery, additionalModules, stylesAndDisruptorsModules, chooseYourFoundationModule, completeYourBed },
            data: { product, productSpecificationData, categoryPaths: { result } }
        } = props;
        {
            /* VSI Customization - START */
        }
        let parentCategory = result && result[0]?.Name ? result[0]?.Name?.toLowerCase() : '';
        parentCategory = parentCategory === 'mattresses' || parentCategory === 'pillows' || parentCategory === 'bedding' ? parentCategory : 'default';
        const dimensionRows = parentCategory === 'pillows' ? 1 : 2;
        const AttributeValues = productSpecificationData.result;
        const productPrice = props.state.productPrice?.CustomerContextualPrice;
        const originalPrice = props.state.productPrice?.AdjustedPrice;
        const discountValue = props.state.productPrice?.DiscountAmount;
        const productId = props.state.productPrice?.ProductId;
        const productIdPrefix = (props.app.config.productIdPrefix && props.app.config.productIdPrefix?.trim()) || '';
        const mfrmProductId = product.result?.ItemId ? `${productIdPrefix}${product.result?.ItemId}` : null;
        const [isLeaseActive, setLeasingButtonActive] = React.useState(true);
        const [activeVariantId, setActiveVariantId] = React.useState<number | undefined>(productId);
        const [activeColorVariantId, setActiveColorVariantId] = React.useState<number | undefined>(productId);
        const [outOfStockSmallParcelState, SetoutOfStockSmallParcelState] = React.useState<boolean>(false);
        // call/chat product
        const isCallChatProduct = productSpecificationData.result?.find(attr => attr.Name?.trim().toLowerCase() === 'call/chat only')
            ?.BooleanValue;
        const sleepExpertSummaryText = productSpecificationData.result?.find(attr => attr.Name?.trim().toLowerCase() === 'sleep expert opinion')
            ?.TextValue;
        const tagsArr = _getBuyboxSEOMetaTags(AttributeValues);
        const [itemOutOfMarket, setItemOutOfMarket] = React.useState(false);
        const [variantOutOfStock, setVariantOutOfStock] = React.useState(false);
        const setActiveVariant = async (variantID: number) => {
            setActiveColorVariantId(variantID);
            setActiveSwatchSelected(variantID, variantOutOfStock, setVariantOutOfStock, props.state.variantAvailibilityList);
            if (outOfStockSmallParcelState) {
                await _smallParcelProductATPCallForInventoryStatus(props, props.data.product.result!.RecordId, props.data.product.result!.ItemId, SetoutOfStockSmallParcelState, true).then().catch();
            }
        };
        const categoryName = props.data.listPageStateBuyBox.result?.categoryName;
        const propsWithLeasingState = {
            ...props,
            setLeasingButtonActive,
            variantAvailibilityList: props.state.variantAvailibilityList,
            setActiveVariant,
            outOfStockSmallParcelState,
            setActiveVariantId
        };
        const renderDimensions = BuyboxProductConfigureMfrm(propsWithLeasingState);
        const showViewCartLink = props.showViewCartLink;
        /* VSI Customization - END */
        const isLoadedPriceComponent = (productPrice && (activeVariantId === product.result?.RecordId || activeColorVariantId === productId)) ? true : false;
        const isLoadedprogressiveLeasingEstimator = progressiveLeasingEstimator && productPrice ? true : false;
        // VWO Tracking
        const {vwoAccountId, vwoSdkKey} = props.app.config;
        if( vwoAccountId && vwoSdkKey ){
            const [ vwoClientInstance, vwoUserId ] = useVwoClientInstance(vwoAccountId, vwoSdkKey );
            if( vwoClientInstance ){
                 const campaignKey = null;
                 const userId = vwoUserId;
                vwoClientInstance.track(campaignKey, userId, 'pdpPageviews');
            }
        }

        return (
            <Module {...ModuleProps}>
                {/* VSI Customization - START - 6432 */}
                <HtmlHeadInclude>
                    {tagsArr?.map(
                        (tag, index) =>
                            tag?.content && <meta name={tag?.name} content={tag?.content} key={index} /*property={tag?.property}*/ />
                    )}
                </HtmlHeadInclude>

                {/* VSI Customization - END - 6432 */}
                <Node {...MediaGalleryContainerProps}>
                    {/* VSI Customization - START */}
                    <Node className={'mfrm-product-badge'}>{productBadges}</Node>
                    {/* VSI Customization - END */}
                    {renderOOBMediaGallery ? mediaGallery : mfrmMediaGallery}
                </Node>

                {/* VSI Customization - END */}
                <Node {...ProductInfoContainerProps}>
                    {/* VSI Customization - START */}
                    <Node className='ms-buybox__content-wrapper' id='mfrm-buybox'>
                        {customerSatisfactionScore}
                        {/* VSI Customization - END */}
                        {title}
                        {/* VSI Customization - START */}
                        <Node className='ms-buybox__price-id-section'>
                            {mfrmProductId && (
                                <Node className='ms-buybox__product-id' tag='span'>
                                    {mfrmProductId}
                                </Node>
                            )}
                            {!props.context.app.config.hideRating && ratingComponent}
                        </Node>
                        {/* Financing Per Month Redesign code starts here */}
                        { }
                        {!isLoadedPriceComponent ?
                            <>
                                <CustomSkeleton type='text' className={`price-skeleton-${parentCategory}`} />
                                <CustomSkeleton type='text' className={`financing-skeleton-${parentCategory}`} />
                            </>
                            : <ErrorBoundary telemetry={props.telemetry}><BuyboxFinancingPerMonth
                                buyboxProps={props}
                                originalPrice={originalPrice}
                                discountValue={discountValue}
                                productPrice={productPrice}
                                categoryName={categoryName}
                                activeVariantId={activeVariantId}
                                productId={productId}
                                activeColorVariantId={activeColorVariantId}
                                isLoadedPriceComponent={isLoadedPriceComponent}
                                parentCategory={parentCategory}
                            /></ErrorBoundary>
                        }
                        {isLoadedprogressiveLeasingEstimator ? (progressiveLeasingEstimator &&
                            productPrice &&
                            <ErrorBoundary telemetry={props.telemetry}> <BuyboxProgressiveLeasingEstimator
                                progressiveLeasingEstimator={progressiveLeasingEstimator}
                                productPrice={productPrice}
                                ctx={props.context.request}
                                leaseState={isLeaseActive}
                            /></ErrorBoundary>) ||
                            null : <CustomSkeleton type='text' className={`progressive-skeleton-${parentCategory}`} />}
                        {/* Financing Per Month Redesign code ends here */}
                        {/*TODO: What is this*/}
                        {inventoryLabel}
                        {(props.state.isEyeBrowLoading) ? <CustomSkeleton className={`promotional-skeleton-${parentCategory}`} type='text' /> :
                            <RichTextComponent className='promotional-message' text={promotionalMessage} />}
                        {/*Delivery By Message and Try In-store Redesign code starts here */}
                        <div>
                            <ErrorBoundary telemetry={props.telemetry}><BuyboxDeliveryByMessage
                                buyboxProps={props}
                                itemOutOfMarket={itemOutOfMarket}
                                setItemOutOfMarket={setItemOutOfMarket}
                                SetoutOfStockSmallParcelState={SetoutOfStockSmallParcelState}
                                outOfStockSmallParcelState={outOfStockSmallParcelState}
                            /></ErrorBoundary>
                            {' '}
                            {props.state.isStoreLoading ? <CustomSkeleton type='text' className={`tryinstore-skeleton-${parentCategory}`} /> : tryInStoreComponent}
                        </div>
                        {/*Delivery By Message and Try In-store Redesign code starts here */}
                        {!props.state.currentProductDimensions?.length && <>
                            <CustomSkeleton type='text' className={`dimension-label-skeleton-${parentCategory}`} />
                            <div className='dimensions-skeleton'>
                                {[...Array(dimensionRows * 3)].map((i, x) => <CustomSkeleton type='image' key={x} />)}
                            </div>
                            <CustomSkeleton type='text' className={`swatch-label-skeleton-${parentCategory}`} />
                            <div className={`swatch-skeleton-${parentCategory}`}>
                                {[...Array(1 * 3)].map((i, x) => <CustomSkeleton circle={true} type='image' key={x} />)}
                            </div></>}
                        {renderDimensions}
                        {chooseYourFoundationModule}
                        {categoryName === "Mattresses" && completeYourBed}
                        {/*TODO: What is this*/}
                        <ErrorBoundary telemetry={props.telemetry}>{keyInPrice}</ErrorBoundary>
                        <>
                            {isCallChatProduct
                                ? <ErrorBoundary telemetry={props.telemetry}><BuyboxCallChat config={props.config} requestContext={props.context.request} /></ErrorBoundary>
                                : addToCart &&
                                <ErrorBoundary telemetry={props.telemetry}> <BuyboxAddToCart
                                    addToCart={addToCart}
                                    buyboxProps={props}
                                    variantOutOfStock={variantOutOfStock}
                                    itemOutOfMarket={itemOutOfMarket}
                                    outOfStockSmallParcelState={outOfStockSmallParcelState}
                                    adjustableItem={adjustableItem}
                                    pillow={pillow}
                                    protector={protector}
                                /></ErrorBoundary>}
                            {showViewCartLink ? <ErrorBoundary telemetry={props.telemetry}><BuyboxViewCartLink {...props} /> </ErrorBoundary> : null}
                        </>
                        {/*TODO: What is this*/}
                        {findInStore && <ErrorBoundary telemetry={props.telemetry}><BuyboxFindInStore {...findInStore} /></ErrorBoundary>}
                    </Node>
                    <ErrorBoundary telemetry={props.telemetry}><BuyboxSocialShare socialShare={props.slots && props.slots.socialShare} /></ErrorBoundary>
                    <ErrorBoundary telemetry={props.telemetry}>{shopSimilarLook}</ErrorBoundary>
                    {mfrmStoreSelector?.[0]}
                    {additionalModules}
                    <ErrorBoundary telemetry={props.telemetry}><BuyboxSleepExpertSummary
                        buyboxProps={props}
                        sleepExpertSummaryText={sleepExpertSummaryText}
                    /></ErrorBoundary>
                    <div className='ms-buybox_sleep-style-disruptors-modules'>
                        {stylesAndDisruptorsModules}
                    </div>
                </Node>
            </Module>
        );
    };

{
    /* VSI Customization - START - 6432 */
}

const _getBuyboxSEOMetaTags = (attributeValues: AttributeValue[] | undefined) => {
    const pageKeywords = _getSEOMetaTagValue(attributeValues, 'pageKeywords');
    const pageKeywordsParser = pageKeywords && getHtmlParserValue(pageKeywords);

    return [{ content: pageKeywordsParser?.toString(), name: 'keywords' }];
};

const _getSEOMetaTagValue = (attributeValues: AttributeValue[] | undefined, keyValue: string) => {
    return attributeValues?.find(currAttribute => currAttribute.Name?.toLowerCase().trim() === keyValue?.toLowerCase().trim())?.TextValue;
};


{
    /* VSI Customization - END - 6432 */
}

export default BuyboxView;
